/*
 * Khung mọi trang bên trong một lớp: menu trái (shadcn Sidebar) + nội dung.
 * Tải dữ liệu lớp một lần (/api/classes/:slug) và chia sẻ qua useClass().
 * Chế độ tập trung (đang làm một product / concept / reflection): ẩn menu.
 * Quy tắc menu: nhãn không xuống dòng, mục đang chọn được tô, chỉ một nhóm mở.
 */
import { createContext, useContext, useEffect, useState } from "react"
import { Link, NavLink, Outlet, useLocation, useParams } from "react-router-dom"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Button } from "@/components/ui/button"
import { Icon, type AnyIconName } from "@/components/icon"
import { LoadingState, PageMotion, UserAvatar } from "@/components/page"
import { api, loginUrl, logoutUrl } from "@/lib/api"
import { cn } from "@/lib/utils"

type ClassState = {
  slug: string
  base: string
  /** Toàn bộ payload /api/classes/:slug: { class, learner, sessions, stickers, certificate, … } */
  data: any
  /** Bật/tắt chế độ tập trung (ẩn menu trái). Dùng useFocusMode(active) cho tiện. */
  setFocusMode: (on: boolean) => void
}
const ClassContext = createContext<ClassState | null>(null)

export function useClass() {
  const v = useContext(ClassContext)
  if (!v) throw new Error("useClass() must be used inside <ClassLayout>")
  return v
}

/** Trang đang ở chế độ tập trung khi `active` = true; tự tắt khi rời trang. */
export function useFocusMode(active: boolean) {
  const { setFocusMode } = useClass()
  useEffect(() => {
    setFocusMode(active)
    return () => setFocusMode(false)
  }, [active, setFocusMode])
}

type NavItem = { id: string; label: string; icon: AnyIconName; count?: (data: any) => number }
const GROUPS: { label: string; items: NavItem[] }[] = [
  {
    label: "Learning",
    items: [
      { id: "start", label: "Start", icon: "flame" },
      { id: "products", label: "Products", icon: "layers" },
      { id: "concepts", label: "Concepts", icon: "lightbulb" },
      { id: "sessions", label: "Schedule", icon: "calendar" },
    ],
  },
  {
    label: "Community",
    items: [
      { id: "classmates", label: "Friends", icon: "users" },
      { id: "submissions", label: "Submissions", icon: "file" },
      { id: "album", label: "Album", icon: "star" },
    ],
  },
  {
    label: "My space",
    items: [
      { id: "portfolio", label: "Portfolio", icon: "user" },
      { id: "rewards", label: "Awards", icon: "award", count: (d) => (d.stickers || []).filter((s: any) => s.earned).length },
      { id: "reflections", label: "Reflections", icon: "notebook" },
    ],
  },
]

const sectionOf = (pathname: string) => pathname.split("/")[3] || "start"
const groupOf = (section: string) => GROUPS.find((g) => g.items.some((i) => i.id === section))?.label ?? null

export function ClassLayout() {
  const { slug = "" } = useParams()
  const { pathname } = useLocation()
  const [data, setData] = useState<any>()
  const [error, setError] = useState("")
  const [focus, setFocusMode] = useState(false)

  useEffect(() => {
    setData(undefined)
    setError("")
    api(`/api/classes/${encodeURIComponent(slug)}`)
      .then(setData)
      .catch((e) => {
        if (e.status === 401) location.href = loginUrl()
        else setError(e.message)
      })
  }, [slug])
  useEffect(() => { setFocusMode(false) }, [pathname])

  if (error)
    return (
      <div className="grid min-h-svh place-items-center bg-sand p-6 text-center">
        <div className="flex max-w-md flex-col items-center gap-4">
          <img src="/brand/sutucon/mascots-2d/meerkat-thinking.svg" alt="" className="size-28" />
          <h1 className="text-2xl font-bold">Không thể mở lớp</h1>
          <p className="text-muted-foreground">{error}</p>
          <Button nativeButton={false} render={<Link to="/my-courses" />}>
            Về khóa học của tôi
          </Button>
        </div>
      </div>
    )
  if (!data)
    return (
      <div className="grid min-h-svh place-items-center bg-sand">
        <LoadingState label="Đang tải lớp học…" />
      </div>
    )

  const ctx: ClassState = { slug, base: `/classes/${slug}`, data, setFocusMode }
  return (
    <ClassContext.Provider value={ctx}>
      <SidebarProvider>
        {!focus && <ClassSidebar />}
        <SidebarInset className="min-h-svh bg-sand">
          {!focus && (
            <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-line bg-card/85 px-4 backdrop-blur md:hidden">
              <SidebarTrigger />
              <span className="truncate font-heading font-bold text-ink">{data.class?.course_name}</span>
            </header>
          )}
          <main className={cn("flex-1 px-4 py-6 sm:px-6 md:px-10 md:py-10", focus && "md:px-6 md:py-6")}>
            <PageMotion motionKey={sectionOf(pathname)}>
              <Outlet />
            </PageMotion>
          </main>
        </SidebarInset>
      </SidebarProvider>
    </ClassContext.Provider>
  )
}

function ClassSidebar() {
  const { base, data } = useClass()
  const { pathname } = useLocation()
  const section = sectionOf(pathname)
  const { setOpenMobile } = useSidebar()
  const [open, setOpen] = useState<string | null>(() => groupOf(section) ?? "Learning")

  useEffect(() => {
    const g = groupOf(section)
    if (g) setOpen(g)
    setOpenMobile(false)
  }, [section, setOpenMobile])

  return (
    <Sidebar className="border-r-[1.5px] border-edge">
      <SidebarHeader className="gap-4 px-5 pt-6 pb-4">
        <Link to="/my-courses" className="flex items-center gap-3">
          <img src="/brand/sutucon/mascots-2d/lion-happy.svg" alt="" className="size-11 shrink-0" />
          <span className="font-heading text-lg font-bold text-ink">Sư Tử Con</span>
        </Link>
        <p className="line-clamp-2 rounded-2xl bg-soft px-3.5 py-2.5 text-sm font-semibold text-accent-foreground">{data.class?.course_name}</p>
      </SidebarHeader>
      <SidebarContent className="gap-1 px-2">
        {GROUPS.map((group) => {
          const isOpen = open === group.label
          return (
            <Collapsible key={group.label} open={isOpen} onOpenChange={(o) => setOpen(o ? group.label : null)}>
              <SidebarGroup className="py-1">
                <CollapsibleTrigger
                  className={cn(
                    "flex h-9 w-full items-center justify-between rounded-full px-3 text-sm font-bold tracking-[0.08em] uppercase transition-colors hover:bg-soft",
                    isOpen ? "text-orange-strong" : "text-muted-foreground",
                  )}
                >
                  {group.label}
                  <Icon name="chevron-down" className={cn("size-4 transition-transform", isOpen && "rotate-180")} />
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarGroupContent className="pt-1">
                    <SidebarMenu className="gap-0.5">
                      {group.items.map((item) => (
                        <SidebarMenuItem key={item.id}>
                          <SidebarMenuButton isActive={section === item.id} render={<NavLink to={`${base}/${item.id}`} />}>
                            <Icon name={item.icon} />
                            <span>{item.label}</span>
                          </SidebarMenuButton>
                          {item.count && <SidebarMenuBadge className="text-sm">{item.count(data)}</SidebarMenuBadge>}
                        </SidebarMenuItem>
                      ))}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </CollapsibleContent>
              </SidebarGroup>
            </Collapsible>
          )
        })}
      </SidebarContent>
      <SidebarFooter className="border-t-[1.5px] border-edge p-3">
        <DropdownMenu>
          <DropdownMenuTrigger className="flex w-full items-center gap-3 rounded-full p-1.5 pr-3 text-left transition-colors hover:bg-soft data-popup-open:bg-soft">
            <UserAvatar user={data.learner} />
            <span className="min-w-0 flex-1 truncate text-sm font-semibold text-ink">{data.learner?.full_name}</span>
            <Icon name="chevron-up" className="size-4 text-muted-foreground" />
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="start" className="w-(--anchor-width) min-w-56">
            <DropdownMenuItem render={<Link to="/settings" />}>
              <Icon name="settings" /> Settings
            </DropdownMenuItem>
            <DropdownMenuItem render={<Link to={`${base}/certificate`} />}>
              <Icon name="graduation" /> Certificate
            </DropdownMenuItem>
            <DropdownMenuItem render={<Link to={`${base}/contributions`} />}>
              <Icon name="chat" /> Feedback
            </DropdownMenuItem>
            <DropdownMenuItem render={<Link to="/my-courses" />}>
              <Icon name="book" /> Khóa học của tôi
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem render={<a href={logoutUrl()} />} className="text-danger">
              <Icon name="x" /> Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>
    </Sidebar>
  )
}
